/**
 * AI CONTEXT
 * Purpose: utility hook to combine multi-query pending/error states into one readiness output.
 * Returns: isReady + flattened error list with source labels.
 * Used by: legacy data hooks with parallel query dependencies.
 * Importance: simple readiness gate helper.
 * Refactor: migrate callers to newer cascading fulfillment hooks where possible.
 */
import { useMemo } from "react"

import { ErrorType } from "#utils/textFormatters"

interface QueryStatus {
  isPending: boolean
  isActive: boolean
  error?: Error | null
  source: string
}

const useCombinedReady = (queries: QueryStatus[]) => {
  // Could use isSuccess + error also
  // What other state is there? Idle?
  const isReady = useMemo(
    () =>
      queries.every(
        ({ isPending, isActive, error }) => !isActive || (!isPending && !error)
      ),
    [queries]
  )

  const errors: ErrorType[] | null = useMemo(() => {
    const combinedErrors = queries
      .filter(({ error }) => error != null)
      .map(
        ({ source, error }) =>
          `${source}: ${
            typeof error === "string"
              ? error
              : (error?.message ?? "Unknown error")
          }`
      )

    return combinedErrors.length > 0 ? combinedErrors : null
  }, [queries])

  return {
    isReady,
    errors,
  }
}

export default useCombinedReady
